import React, { ReactNode, RefObject } from 'react';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { useSearchParams } from 'react-router';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';

type ViewModeSwitch = {
  headlineText?: string | ((searchParams: URLSearchParams) => string);
  headingRef?: RefObject<HTMLHeadingElement | null>;
  headlineInvisible?: boolean
  showSwitches?: boolean;
  switches: { href: string; text: string; default?: boolean }[];
  children?: (searchParams: URLSearchParams) => ReactNode;
};

export function ViewModeSwitch({
  headlineText,
  headlineInvisible,
  headingRef,
  showSwitches = true,
  switches,
  children,
}: ViewModeSwitch) {
  const [searchParams] = useSearchParams();
  const id = useHtmlValidId();

  return (
    <>
      <div className="view-mode-switch">
        {headlineText
          ? (
              <h2
                ref={headingRef}
                className={`view-mode-switch__headline${headlineInvisible ? ' invisible' : ''}`}
                tabIndex={-1}
              >
                {typeof headlineText === 'function' ? headlineText(searchParams) : headlineText}
              </h2>
            )
          : null}
        <div className="view-mode-switch__view">
          {showSwitches &&
            switches.map((switcher) => {
              const searchParamsOfHref = new URLSearchParams(switcher.href?.split('?')?.at(1));
              const getAriaCurrent = () => {
                const hrefParams = Array.from(searchParamsOfHref.entries());

                // No params in passed href -> check for default
                if (hrefParams.length === 0) {
                  return searchParams.size === 0 && switcher.default ? 'page' : undefined;
                }

                // No params in browser location -> check for default
                if (searchParams.size === 0) {
                  return switcher.default ? 'page' : undefined;
                }

                // Params in href and location -> compare them
                return hrefParams.every(([key, value]) => searchParams.has(key) && searchParams.get(key) === value)
                  ? 'page'
                  : undefined;
              };

              return (
                <Btn
                  key={switcher.href}
                  href={switcher.href}
                  modifier={[BtnModifier.GROW, BtnModifier.TOGGLE]}
                  text={switcher.text}
                  ariaControls={id}
                  ariaCurrent={getAriaCurrent()}
                />
              );
            })}
        </div>
      </div>
      <div id={id}>{children && typeof children === 'function' ? children(searchParams) : null}</div>
    </>
  );
}
